import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useApply } from "../../context/ApplyContext";
import { getAttributionForSubmission } from "../../lib/attribution";
import { useT } from "../../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const initialForm = {
  role: "",
  challenge: "",
  readiness: "",
  name: "",
  email: "",
  phone: "",
  company: "",
  referral_source: "",
  referral_detail: "",
  notes: "",
};

export const ApplyModal = () => {
  const { isOpen, close, markApplied } = useApply();
  const t = useT();
  const tApply = t.apply;
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const dialogRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    setTimeout(() => dialogRef.current?.focus(), 30);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [isOpen, close]);

  useEffect(() => {
    if (isOpen && submitted) {
      setStep(1);
      setForm(initialForm);
      setSubmitted(false);
      setError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));

  const step1Valid = form.role && form.challenge && form.readiness;
  const step2Valid =
    form.name.trim().length >= 2 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());

  const onNext = () => {
    if (!step1Valid) {
      setError(tApply.err1);
      return;
    }
    setError("");
    setStep(2);
  };

  const onBack = () => {
    setError("");
    setStep(1);
  };

  const referralDetailPlaceholder = {
    friend: tApply.friendDetail,
    other: tApply.otherDetail,
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!step2Valid) {
      setError(tApply.err2);
      return;
    }
    setError("");
    setSubmitting(true);
    try {
      await axios.post(`${API}/applications`, {
        ...form,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        company: form.company.trim(),
        referral_detail:
          form.referral_source === "friend" || form.referral_source === "other"
            ? form.referral_detail.trim()
            : "",
        notes: form.notes.trim(),
        ...getAttributionForSubmission(),
      });
      markApplied();
      setSubmitted(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || tApply.errGeneric;
      setError(typeof msg === "string" ? msg : tApply.errGeneric);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="sp-modal-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
      data-testid="apply-modal-overlay"
    >
      <div
        className="sp-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="apply-modal-title"
        tabIndex={-1}
        ref={dialogRef}
        data-testid="apply-modal"
      >
        <button
          type="button"
          className="sp-modal-close"
          onClick={close}
          aria-label={tApply.closeLabel}
          data-testid="apply-modal-close"
        >
          ×
        </button>

        {!submitted ? (
          <>
            <header className="sp-modal-head">
              <span className="sp-eyebrow">
                {step === 1 ? tApply.step1Eyebrow : tApply.step2Eyebrow}
              </span>
              <h2 id="apply-modal-title" className="sp-modal-title">
                {step === 1 ? (
                  <>
                    {tApply.step1TitlePre}
                    <em>{tApply.step1TitleEm}</em>
                    {tApply.step1TitlePost}
                  </>
                ) : (
                  tApply.step2Title
                )}
              </h2>
              <p className="sp-modal-sub">
                {step === 1 ? tApply.step1Sub : tApply.step2Sub}
              </p>
              <div className="sp-stepper" aria-hidden="true">
                <span className={step >= 1 ? "is-on" : ""} />
                <span className={step >= 2 ? "is-on" : ""} />
              </div>
            </header>

            {step === 1 && (
              <div className="sp-modal-body" data-testid="apply-step-1">
                <Field label={tApply.labelRole}>
                  <RadioGroup
                    name="role"
                    value={form.role}
                    onChange={update("role")}
                    options={tApply.roles}
                    testidPrefix="apply-role"
                  />
                </Field>

                <Field label={tApply.labelChallenge}>
                  <RadioGroup
                    name="challenge"
                    value={form.challenge}
                    onChange={update("challenge")}
                    options={tApply.challenges}
                    testidPrefix="apply-challenge"
                  />
                </Field>

                <Field label={tApply.labelReadiness}>
                  <RadioGroup
                    name="readiness"
                    value={form.readiness}
                    onChange={update("readiness")}
                    options={tApply.readiness}
                    testidPrefix="apply-readiness"
                  />
                </Field>

                {error && <p className="sp-modal-error" role="alert">{error}</p>}

                <div className="sp-modal-actions">
                  <button
                    type="button"
                    className="sp-btn sp-btn-ghost"
                    onClick={close}
                    data-testid="apply-cancel"
                  >
                    {tApply.cancel}
                  </button>
                  <button
                    type="button"
                    className="sp-btn sp-btn-primary"
                    onClick={onNext}
                    disabled={!step1Valid}
                    data-testid="apply-next"
                  >
                    {tApply.next} <span className="arrow">→</span>
                  </button>
                </div>
              </div>
            )}

            {step === 2 && (
              <form
                className="sp-modal-body"
                onSubmit={onSubmit}
                data-testid="apply-step-2"
              >
                <div className="sp-input-row">
                  <Field label={tApply.labelName}>
                    <input
                      className="sp-input"
                      value={form.name}
                      onChange={update("name")}
                      autoComplete="name"
                      required
                      data-testid="apply-name"
                    />
                  </Field>
                  <Field label={tApply.labelEmail}>
                    <input
                      className="sp-input"
                      type="email"
                      value={form.email}
                      onChange={update("email")}
                      autoComplete="email"
                      required
                      data-testid="apply-email"
                    />
                  </Field>
                </div>

                <div className="sp-input-row">
                  <Field label={tApply.labelPhone}>
                    <input
                      className="sp-input"
                      type="tel"
                      value={form.phone}
                      onChange={update("phone")}
                      autoComplete="tel"
                      placeholder={tApply.phonePlaceholder}
                      data-testid="apply-phone"
                    />
                  </Field>
                  <Field label={tApply.labelCompany}>
                    <input
                      className="sp-input"
                      value={form.company}
                      onChange={update("company")}
                      autoComplete="organization"
                      data-testid="apply-company"
                    />
                  </Field>
                </div>

                <Field label={tApply.labelReferral}>
                  <RadioGroup
                    name="referral_source"
                    value={form.referral_source}
                    onChange={(e) => {
                      const v = e.target.value;
                      setForm((f) => ({
                        ...f,
                        referral_source: v,
                        referral_detail:
                          v === "friend" || v === "other"
                            ? f.referral_detail
                            : "",
                      }));
                    }}
                    options={tApply.referrals}
                    testidPrefix="apply-referral"
                  />
                  {(form.referral_source === "friend" ||
                    form.referral_source === "other") && (
                    <input
                      className="sp-input"
                      style={{ marginTop: "0.6rem" }}
                      value={form.referral_detail}
                      onChange={update("referral_detail")}
                      placeholder={referralDetailPlaceholder[form.referral_source]}
                      maxLength={200}
                      data-testid="apply-referral-detail"
                    />
                  )}
                </Field>

                <Field label={tApply.labelNotes}>
                  <textarea
                    className="sp-input sp-textarea"
                    rows={4}
                    value={form.notes}
                    onChange={update("notes")}
                    placeholder={tApply.notesPlaceholder}
                    data-testid="apply-notes"
                  />
                </Field>

                {error && <p className="sp-modal-error" role="alert">{error}</p>}

                <div className="sp-modal-actions">
                  <button
                    type="button"
                    className="sp-btn sp-btn-ghost"
                    onClick={onBack}
                    data-testid="apply-back"
                  >
                    ← {tApply.back}
                  </button>
                  <button
                    type="submit"
                    className="sp-btn sp-btn-primary"
                    disabled={submitting || !step2Valid}
                    data-testid="apply-submit"
                  >
                    {submitting ? tApply.submitting : tApply.submit}
                    {!submitting && <span className="arrow">→</span>}
                  </button>
                </div>
              </form>
            )}
          </>
        ) : (
          <div className="sp-modal-success" data-testid="apply-success">
            <span className="sp-eyebrow">{tApply.success.eyebrow}</span>
            <h2 className="sp-modal-title">
              {tApply.success.titlePre}
              <em>{tApply.success.titleEm}</em>
            </h2>
            <p className="sp-modal-sub">{tApply.success.body}</p>
            <div className="sp-modal-actions sp-modal-actions--center">
              <button
                type="button"
                className="sp-btn sp-btn-primary"
                onClick={close}
                data-testid="apply-success-close"
              >
                {tApply.success.close}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <label className="sp-field">
    <span className="sp-field-label">{label}</span>
    {children}
  </label>
);

const RadioGroup = ({ name, value, onChange, options, testidPrefix }) => (
  <div className="sp-radio-group" role="radiogroup" aria-label={name}>
    {options.map((opt) => {
      const checked = value === opt.value;
      return (
        <label
          key={opt.value}
          className={`sp-radio ${checked ? "is-checked" : ""}`}
          data-testid={`${testidPrefix}-${opt.value}`}
        >
          <input
            type="radio"
            name={name}
            value={opt.value}
            checked={checked}
            onChange={onChange}
          />
          <span className="sp-radio-dot" aria-hidden="true" />
          <span className="sp-radio-label">{opt.label}</span>
        </label>
      );
    })}
  </div>
);
